/** Tab 1: Basic Details */
import { useState } from 'react'
import {
  Button, Card, Form, Input, message, Space, DatePicker, Select, Row, Col,
} from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { joiningApi, type JoiningDetail } from '@/features/hr/joiningApi'

interface Props { detail: JoiningDetail }

export default function BasicDetailsTab({ detail }: Props) {
  const [form] = Form.useForm()
  const qc = useQueryClient()
  const [dirty, setDirty] = useState(false)

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => joiningApi.update(detail.id, values),
    onSuccess: () => {
      message.success('Saved')
      setDirty(false)
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
  })

  const initial: Record<string, unknown> = { ...detail.employee }
  if (initial.date_of_birth) initial.date_of_birth = dayjs(initial.date_of_birth as string)

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initial}
      onValuesChange={() => setDirty(true)}
      onFinish={(values) => {
        const out: Record<string, unknown> = {}
        Object.keys(values).forEach((k) => {
          const v = values[k]
          out[k] = dayjs.isDayjs(v) ? v.format('YYYY-MM-DD') : v
        })
        save.mutate(out)
      }}
    >
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="middle_name" label="Middle Name">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="last_name" label="Last Name">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="father_or_husband_name" label="Father/Husband Name">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="gender" label="Gender">
              <Select
                options={[
                  { value: 'MALE', label: 'Male' },
                  { value: 'FEMALE', label: 'Female' },
                  { value: 'OTHER', label: 'Other' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="date_of_birth" label="Date of Birth">
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="marital_status" label="Marital Status">
              <Select
                options={[
                  { value: 'SINGLE', label: 'Single' },
                  { value: 'MARRIED', label: 'Married' },
                  { value: 'DIVORCED', label: 'Divorced' },
                  { value: 'WIDOWED', label: 'Widowed' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="blood_group" label="Blood Group">
              <Select
                allowClear
                placeholder="Select"
                options={['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(
                  (g) => ({ value: g, label: g }),
                )}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="email" label="Email">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="phone" label="Phone">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="alternate_mobile" label="Alternate Mobile">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="nationality" label="Nationality">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="religion" label="Religion">
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Space>
        <Button type="primary" htmlType="submit" disabled={!dirty} loading={save.isPending}>
          Save
        </Button>
        {dirty && <span style={{ color: '#faad14' }}>Unsaved changes</span>}
      </Space>
    </Form>
  )
}
